import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import ClientLanding from './pages/ClientLanding';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import ClientAuth from './pages/ClientAuth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DriverResetPassword from './pages/DriverResetPassword';
import ChangePassword from './pages/ChangePassword';
import AdminLogin from './pages/AdminLogin';
import DriverLogin from './pages/DriverLogin';
import StaffPortalLogin from './pages/StaffPortalLogin';
import AuthCallback from './pages/AuthCallback';

const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const DriverDashboard = lazy(() => import('./pages/DriverDashboard'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[40vh] w-full items-center justify-center text-gray-500 text-sm" aria-busy="true">
      Loading…
    </div>
  );
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const isDev = process.env.NODE_ENV === 'development';

function App() {
  useEffect(() => {
    if (window.google?.maps?.places) return;
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) return;

    if (!window.google && GOOGLE_MAPS_API_KEY) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=es`;
      script.async = true;
      script.defer = true;
      script.onload = () => { if (isDev) console.debug('Google Maps API loaded'); };
      script.onerror = () => { if (isDev) console.warn('Google Maps API failed to load'); };
      document.head.appendChild(script);
    }
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
          <Route path="/" element={<ClientLanding />} />
          <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
          <Route path="/politica-de-cookies" element={<CookiesPolicy />} />
          <Route path="/login" element={<ClientAuth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route path="/dashboard" element={<ClientDashboard />} />
          <Route path="/portal/login" element={<StaffPortalLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/servicio/:id" element={<ServiceDetailPage />} />
          <Route path="/driver/login" element={<DriverLogin />} />
          <Route path="/driver/dashboard" element={<DriverDashboard />} />
          <Route path="/driver/reset-password" element={<DriverResetPassword />} />
          <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ErrorBoundary>
    </div>
  );
}

export default App;
